import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { IAuthenticatedSession } from '@kinet-open-network/shared/data-access/model';
import { Store } from '@ngrx/store';
import {
  EMPTY,
  Observable,
  catchError,
  filter,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { AuthDeviceActions } from '../+state/auth.actions';
import { AuthenticatedUser } from '../models/authenticated-user.model';
import { ExpirationReason } from '../models/expiration-reason.interface';
import { AuthService } from './auth.service';
import { AuthStorageService } from './token-storage.service';

/**
 * Time in milliseconds before the access token expires
 */
export const REFRESH_BUFFER_TIME = 30 * 1000; // 30 seconds

export const REFRESH_BLACKLIST = ['/auth/login', '/auth/refresh'];

/**
 * Interceptor prida do hlavicky requestu token a obnovuje ho
 *
 * Flow:
 *  1. Skontroluje ci do aktualneho requestu ma pridat token
 *  2. Skontroluje ci ma obnovit token - t.j. pozrie sa na aktualny access token a zisti ci je exspirovany
 *     alebo ci je blizko exspiracie
 *  3. V pripade ak je exspirovany alebo blizko exspiracie, zavola pred samptnym requestom refresh tokenu
 *     a az potom posle request s novym tokenom
 *  4. V pripade ze bol refresh token neplatny alebo sa nieco pokazilo na strane servera pri refreshovani tokenu
 *     tak je pouzivatel odhlaseny a je presmerovany na login stranku
 *  5. V pripade ze sa dalse request v case ked sa token obnovuje, tento request je zadrzany a odoslany
 *     az po uspesnom obnoveni tokenu, alebo sa ani nezavola ak sa token neobnovi
 *  6. V pripade, ze refresh sice nebol pred requestom nutny, ale dostaneme 401, tak sa pokusim znova refreshnut
 *     token a request odoslat znova - toto moze nastat napriklad ak bol pouzivatel odhlaseny na strane servera
 *     napriklad pri zmene práv a podobne
 *
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);
  private authStorage = inject(AuthStorageService);
  private store = inject(Store);

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (this.shouldIgnore(req)) {
      return next.handle(req);
    }

    if (this.shouldRefresh()) {
      return this.handleRefresh(req, next);
    }

    return this.handleRequest(req, next);
  }

  private handleRequest(req: HttpRequest<unknown>, next: HttpHandler) {
    if (this.authService.isRefreshing$.value === false) {
      return next
        .handle(this.addToken(req))
        .pipe(catchError((error) => this.handleError(error, req, next)));
    }

    return this.authService.isRefreshing$.pipe(
      filter((state) => state === false),
      take(1),
      switchMap(() =>
        next
          .handle(this.addToken(req))
          .pipe(catchError((error) => this.handleError(error, req, next))),
      ),
    );
  }

  private addToken(
    req: HttpRequest<unknown>,
    accessToken: string | null = null,
  ) {
    accessToken = accessToken || this.authStorage.getAccessToken();

    if (accessToken) {
      return req.clone({
        setHeaders: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
    }

    return req;
  }

  private handleRefresh(req: HttpRequest<unknown>, next: HttpHandler) {
    if (this.authService.isRefreshing$.value === false) {
      const refreshToken = this.authStorage.getRefreshToken();

      if (!refreshToken) {
        return this.handleExpired('TOKEN_EXPIRED');
      }

      const exp = this.authService.getTokenExpiration(refreshToken);

      if (exp < Date.now()) {
        return this.handleExpired('TOKEN_EXPIRED');
      }

      return this.authService.refresh(refreshToken).pipe(
        tap(({ data }) => this.handleRefreshed(data)),
        switchMap((res) =>
          next.handle(this.addToken(req, res.data.accessToken)),
        ),
        catchError(() => this.handleExpired('TOKEN_REVOKED')),
      );
    } else {
      return this.authService.isRefreshing$.pipe(
        filter((state) => state === false),
        take(1),
        switchMap(() => next.handle(this.addToken(req))),
        catchError(() => this.handleExpired('TOKEN_REVOKED')),
      );
    }
  }

  private handleError(
    error: unknown,
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ) {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 401) {
        return this.handleRefresh(req, next);
      }
    }

    return throwError(() => error);
  }

  private handleRefreshed(data: IAuthenticatedSession) {
    const authenticatedUser = AuthenticatedUser.fromResponse({
      ...data.user,
      abilities: data.abilities,
    });

    this.store.dispatch(
      AuthDeviceActions.refreshed({
        tokens: {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
        },
        user: authenticatedUser,
      }),
    );
  }

  private handleExpired(reason: ExpirationReason = 'UNKNOWN') {
    this.store.dispatch(AuthDeviceActions.expired({ reason }));

    return EMPTY;
  }

  private shouldRefresh() {
    const accessToken = this.authStorage.getAccessToken();

    if (!accessToken) {
      return false;
    }

    const exp = this.authService.getTokenExpiration(accessToken);

    return exp - Date.now() < REFRESH_BUFFER_TIME;
  }

  private shouldIgnore(req: HttpRequest<unknown>) {
    return REFRESH_BLACKLIST.some((path) => req.url.includes(path));
  }
}
